import { useRef, useState, useEffect } from 'react';
import { Vector3, CatmullRomCurve3 } from 'three';
import { useFrame } from '@react-three/fiber';
import { useLocation } from 'react-router-dom';
import Cabin from '../assets/truck/Cabin.jsx';
import Trailer from '../assets/truck/Trailer.jsx';
import FlockL from '../assets/truck/FlockL.jsx';
import FlockR from '../assets/truck/FlockR.jsx';
import Batteries from '../assets/truck/Batteries.jsx';
import TruckWheels from '../assets/truck/TruckWheels.jsx';
import * as roadsPath from '../assets/coordinates/roadsPath.json';
import Ccr from './Ccr.jsx';
import gsap from 'gsap';



const adjustedPoints = roadsPath.truck.map((pos) =>
    new Vector3(parseFloat(pos.x) - 4, parseFloat(pos.y), parseFloat(pos.z) - 59.1)
);
const spline = new CatmullRomCurve3(adjustedPoints);



export default function Truck({ setTruckPos, position = [0, 0, 0], rotation = [0, 0 , 0], scale = [1.2, 1.2, 1.2], isMooving = false }) {
    const [isCcr, setIsCcr] = useState(false);
    const [isCcrVisible, setIsCcrVisible] = useState(false);
    const [isBatteries, setIsBatteries] = useState(false);
    const groupRef = useRef();
    const location = useLocation();
    const truckPosRef = useRef(0.106);
    const trailerRef = useRef();
    const cabinRef = useRef();
    const flockRRef = useRef();
    const flockLRef = useRef();
    const batteriesRef = useRef();
    const wheelsRefs = Array.from({ length: 5 }, () => useRef());

    const setOpacity = (visible, transparent, ccr) => {
        gsap.to(trailerRef.current.material, { opacity: transparent, duration: 1 });
        gsap.to(cabinRef.current.material, { opacity: transparent, duration: 1 });
        gsap.to(flockRRef.current.material, { opacity: transparent, duration: 1 });
        gsap.to(flockLRef.current.material, { opacity: transparent, duration: 1 });
        gsap.to(batteriesRef.current.glowBlueMaterial, { opacity: visible, duration: 1 });
        gsap.to(batteriesRef.current.baseMaterial, { opacity: visible, duration: 1 });
        setIsCcrVisible(ccr);
        wheelsRefs.forEach(ref => {
            gsap.to(ref.current.material, { opacity: transparent, duration: 1 });
        });
    };

    useEffect(() => {
        switch (location.pathname) {
            case "/captation":
                setOpacity(0.2, 0.2, true);
                break;
            case "/charge":
                setOpacity(0.8, 0.25, true);
                break;
            default:
                truckPosRef.current = 0.20;
                setOpacity(1, 1, true);
                break;
        }
    }, [location.pathname]);


    useFrame((state, delta) => {
        if (isMooving) {
            let speed = 0.028 * delta;
            if (location.pathname === "/smartpowering") {
                speed = 0.020 * delta;
                truckPosRef.current += speed;
                if (truckPosRef.current > 0.34) {
                    truckPosRef.current = 0.22;
                }
            } else if (location.pathname === "/captation" || location.pathname === "/charge") {
                speed = 0.001 * delta;
                truckPosRef.current += speed;
                if (truckPosRef.current > 0.131) {
                    setOpacity(0, 0, false);
                }
                if (truckPosRef.current > 0.132) {
                    truckPosRef.current = 0.106;
                    setOpacity(0.8, 0.25, true);
                }
            } else {
                truckPosRef.current = (truckPosRef.current + speed) % 1;
            }

            setTruckPos(truckPosRef.current);

            const newPosition = spline.getPoint(truckPosRef.current);
            groupRef.current.position.copy(newPosition);

            const target = spline.getPoint((truckPosRef.current + 0.002) % 1);
            groupRef.current.lookAt(target);

            if (truckPosRef.current >= 0.1154 && truckPosRef.current <= 0.353280) {
                setIsCcr(true);
            } else {
                setIsCcr(false);
            }
            if(location.pathname === "/charge"){
                if (truckPosRef.current >= 0.1160 && truckPosRef.current <= 0.353280) {
                    setIsBatteries(true);
                }
                else {
                    setIsBatteries(false);
                }
            }
            else {
                setIsBatteries(false);
            }
        }
    });

    return (
        <>
            <group ref={groupRef} position={position} rotation={[0, Math.PI , 0]} scale={scale}>
                <Batteries isActive={isBatteries} ref={batteriesRef} position={[0, 1, -5]} />
                <TruckWheels ref={wheelsRefs[0]} position={[0, 0.23, 1.54]} isRotate={isMooving} />
                <TruckWheels ref={wheelsRefs[1]} position={[0, 0.23, 5.1]} isRotate={isMooving} />
                <TruckWheels ref={wheelsRefs[2]} position={[0, 0.23, -3.31]} isRotate={isMooving} />
                <TruckWheels ref={wheelsRefs[3]} position={[0, 0.23, -4.35]} isRotate={isMooving} />
                <TruckWheels ref={wheelsRefs[4]} position={[0, 0.23, -5.4]} isRotate={isMooving} />
                <Cabin ref={cabinRef} position={[0, 1, 2]} />
                <FlockL ref={flockLRef} position={[-1, 0, 3]} />
                <FlockR ref={flockRRef} position={[1, 0, 3]} />
                <Trailer ref={trailerRef} position={[0, 1, -10]} />
                <Ccr position={[0,0.24,4.4]} isActive={isCcr} isVisible={isCcrVisible}/>
            </group>
        </>
    );
}
